import axios from 'axios';
import {
  flow,
  getEnv,
  getRoot,
  getSnapshot,
  resolveIdentifier,
  types,
} from 'mobx-state-tree';

import globalUserMessages from '../i18n/globalUserMessages';
import Product from '../models/Product';
import StatefulStore from '../models/StatefulStore';
import Modal from '../models/modal/Modal';
import AddressBookModal from '../models/modal/AddressBookModal';
import OrderCheckout from '../models/order/checkout/OrderCheckout';
import RequestState, { RequestStateType } from '../types/RequestState';
import CheckoutPageModule from '../types/checkout/CheckoutPageModule';
import { globalUserMessageDataKey } from '../util/constants';
import dispatchFlashAlert from '../util/dispatchFlashAlert';
import { blockScroll, unblockScroll } from '../util/dom';

const CheckoutCartModal = Modal.named('CheckoutCartModal', {
  selectedProduct: types.maybeNull(types.reference(Product)),
  position: types.maybeNull(types.integer),
}).actions((self) => {
  return {
    setup: (productId, position) => {
      self.selectedProduct = resolveIdentifier(
        Product,
        getRoot(self).productStore.products,
        productId
      );
      self.position = position;
    },
    close: () => {
      unblockScroll();
      self.isOpen = false;
    },
    open: () => {
      blockScroll();
      self.isOpen = true;
    },
  };
});

const CheckoutStore = StatefulStore.named('CheckoutStore')
  .props({
    order: types.maybeNull(OrderCheckout),
    modulesState: types.optional(types.map(RequestStateType), {}),
    checkoutCartModal: types.optional(CheckoutCartModal, {
      isOpen: false,
    }),
    addressBookModal: types.optional(AddressBookModal, {
      isOpen: false,
    }),
    registrationStatus: types.maybeNull(
      types.model('RegistrationStatus', {
        messages: types.optional(types.array(types.string), []),
        status: types.string,
        success: types.boolean,
      })
    ),
    scrollToAlertCounter: types.optional(types.number, 0),
  })
  .actions((self) => {
    let source;
    let registrationAlertTimeout;

    const convertItemsToMap = (items) => {
      return items.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
    };

    const transformPayload = (additionalProduct) => {
      return {
        customer_fields: self.order.customer_fields,
        extra_fields: self.order.extra_fields,
        shipping_customer_fields: self.order.shipping_customer_fields,
        products: self.order.productList,
        additional_product: additionalProduct,
        selected_payment: self.order.selectedPayment,
        selected_shipping: self.order.selectedShipping,
      };
    };

    const updateOrderFields = (order) => {
      // Update active currency
      getRoot(self).currencyStore.setCurrencyCode(order.currency);

      // Check if pickupPoints had data before update
      const hadPickupPoints =
        self.order?.pickupPoints && self.order.pickupPoints.size > 0;

      // Preserve pickup points if they've been already fetched
      // Use getSnapshot to get plain values before replacement to avoid detached references
      const pickupPointsSnapshot = self.order?.pickupPoints
        ? getSnapshot(self.order.pickupPoints)
        : undefined;
      const pickupPointStateSnapshot = self.order?.pickupPointState
        ? getSnapshot(self.order.pickupPointState)
        : undefined;

      // Update order with preserved pickup points data
      // Note: selected_shipping.selection_fields transformation is handled by SelectedShipping.preProcessSnapshot
      const orderWithPickupPoints = {
        ...order,
        ...(pickupPointsSnapshot && { pickupPoints: pickupPointsSnapshot }),
        ...(pickupPointStateSnapshot && {
          pickupPointState: pickupPointStateSnapshot,
        }),
      };

      self.order = orderWithPickupPoints;

      self.order.customer_fields.replace(
        convertItemsToMap(order.customer_fields)
      );
      self.order.extra_fields.replace(convertItemsToMap(order.extra_fields));
      self.order.shipping_customer_fields.replace(
        convertItemsToMap(order.shipping_customer_fields)
      );

      // Re-resolve selected_shipping.module reference if it exists
      // This ensures the reference points to the correct object in the new order
      if (self.order.selected_shipping?.module && self.order.shipping_options) {
        try {
          const moduleId = self.order.selected_shipping.module.id;
          // Try to re-resolve the reference to ensure it's valid
          self.order.selected_shipping.updateSelectedModule({
            id: moduleId,
            options: self.order.shipping_options,
          });
          self.order.selected_shipping.ensureSelectionFieldsFromModule(
            self.order.selected_shipping.module
          );
        } catch (error) {
          // If reference can't be resolved, clear it
          // This happens when the shipping option no longer exists
          if (self.order.selected_shipping) {
            self.order.selected_shipping.module = null;
          }
        }
      }

      // If pickupPoints was cleared but should have data, trigger fetch
      const pickupPointsNowEmpty =
        !self.order.pickupPoints || self.order.pickupPoints.size === 0;
      let requiresPickupPoint = false;
      try {
        requiresPickupPoint =
          self.order?.selected_shipping?.module?.requires_pickup_point || false;
      } catch (error) {
        // Reference is invalid, assume no pickup point required
        requiresPickupPoint = false;
      }

      if (hadPickupPoints && pickupPointsNowEmpty && requiresPickupPoint) {
        // Pickup points were cleared, trigger fetch
        self.order.fetchPickupPoints();
      }

      dispatchFlashAlertIfAny();
    };

    const dispatchFlashAlertIfAny = () => {
      if (window.isSSR) {
        return;
      }
      const alerts = self.order?.alerts ?? self.order?.data?.alerts;
      if (!alerts?.length) {
        return;
      }
      const firstFlashAlert = alerts.find((alert) => alert.flash === true);
      if (firstFlashAlert) {
        self.dispatchEvent({
          type: globalUserMessageDataKey.globalUserMessage,
          detail: {
            allowHtml: true,
            defaultMessage: firstFlashAlert.message,
            type: firstFlashAlert.type,
          },
        });
      }
    };

    return {
      addProductToOrder: flow(function* addProductToOrder({
        product,
        quantity = 1,
        activeProductId,
      }) {
        const additionalProduct = {
          id: activeProductId || product.id,
          quantity,
          is_free_of_charge: product.is_free_of_charge || false,
          category_id: product.main_category_id,
        };

        yield self.validateOrder({
          module: CheckoutPageModule.CART_MODULE,
          additionalProduct,
        });
      }),
      registerCustomer: flow(function* registerCustomer() {
        const module = 'REGISTRATION';
        self.modulesState.set(module, RequestState.LOADING);

        const payload = {
          productListProducts: self.order.productList,
        };

        try {
          yield getEnv(self)
            .apiWrapper.apiAxios()
            .post(`checkout/register-customer`, payload);

          self.modulesState.set(module, RequestState.LOADED);
          window.location.reload();
        } catch (error) {
          self.modulesState.set(module, RequestState.ERROR);
          self.registrationStatus = error.response?.data?.messages
            ? error.response?.data
            : null;
          registrationAlertTimeout = setTimeout(() => {
            self.resetRegistrationAlert();
          }, 5000);
          throw error;
        }
      }),
      resetRegistrationAlert: () => {
        self.registrationStatus = null;
        clearTimeout(registrationAlertTimeout);
        registrationAlertTimeout = null;
        self.modulesState.set('REGISTRATION', RequestState.NONE);
      },
      loadOrder: flow(function* loadOrder() {
        self.setLoading(true);

        try {
          const timestamp = new Date().getTime();
          const order = yield getEnv(self).apiWrapper.request(`checkout`, {
            params: {
              t: timestamp,
            },
          });

          self.order = order;
          dispatchFlashAlert(self.dispatchEvent, order);

          // Check if we need to fetch pickup points after loading order
          const pickupPointIsRequired =
            self.order?.selected_shipping?.module?.requires_pickup_point;
          const hasNoPickupPoints =
            !self.order?.pickupPoints || self.order.pickupPoints.size === 0;

          // If pickup point is required but we don't have data, fetch it
          if (pickupPointIsRequired && hasNoPickupPoints) {
            yield self.order.fetchPickupPoints();

            if (self.order._pickupPointNeedsSync) {
              self.order._pickupPointNeedsSync = false; // Reset flag
              yield self.updateOrder({
                module: CheckoutPageModule.SHIPPING_METHOD_MODULE,
              });
            }
          }
        } catch (error) {
          if (error.response?.status === 403) {
            self.dispatch403Message();
          } else {
            self.setError(error);
          }
          throw error;
        }
        self.setLoading(false);
      }),
      confirmOrder: flow(function* confirmOrder(module, additionalProduct) {
        try {
          self.modulesState.set(module, RequestState.LOADING);

          const payload = transformPayload(additionalProduct);

          const response = yield getEnv(self)
            .apiWrapper.apiAxios()
            .post(`checkout/confirm`, payload);

          updateOrderFields(response.data);
          self.modulesState.set(module, RequestState.LOADED);
        } catch (error) {
          if (error.response?.status === 403) {
            self.dispatch403Message();
          } else {
            // Update order to show backend error alerts.
            updateOrderFields(error.response.data);
            self.scrollToAlertCounter++;
          }
          self.modulesState.set(module, RequestState.ERROR);
          throw error;
        }
      }),
      updateOrder: flow(function* updateOrder({ module, additionalProduct }) {
        if (self.order.productList.length === 0) {
          return;
        }
        self.cancelOrder();

        self.modulesState.set(module, RequestState.LOADING);

        const payload = transformPayload(additionalProduct);

        try {
          const response = yield getEnv(self)
            .apiWrapper.apiAxios()
            .put(`checkout`, payload, {
              cancelToken: source.token,
            });

          updateOrderFields(response.data);
          self.resetRegistrationAlert();
          yield self.validateShipping();
          self.modulesState.set(module, RequestState.LOADED);
        } catch (error) {
          if (error.response?.status === 403) {
            self.dispatch403Message();
          } else {
            // We do not set error when cancel occurs as the cancel is caused
            // by another API query that will handle the state properly.
            if (!axios.isCancel(error)) {
              throw error;
            }
          }
          self.modulesState.set(module, RequestState.ERROR);
        }
      }),
      cancelOrder: () => {
        if (source) {
          source.cancel();
        }

        source = axios.CancelToken.source();
      },
      dispatch403Message: () => {
        const content = {
          type: globalUserMessageDataKey.globalUserMessage,
          detail: {
            forceRefresh: true,
            ...globalUserMessages.pageExpired,
          },
        };
        self.dispatchEvent(content);
      },
      validateOrder: flow(function* validateOrder(args) {
        yield self.updateOrder(args);
      }),
      validateShipping: flow(function* validateShipping() {
        if (self.order.cartIsEmpty) {
          return;
        }

        const pickupPointIsRequired =
          self.order.selected_shipping?.module?.requires_pickup_point || null;

        if (!pickupPointIsRequired) {
          return;
        }

        const currentState = self.order.currentPickupPointState;
        const hasPointsForCurrentIdentifier =
          currentState === RequestState.LOADED;
        const currentPickupPoint = self.order.selected_shipping?.pickup_point;
        const currentList = self.order.currentPickupPointList;
        const currentPickupPointExistsInList =
          currentPickupPoint &&
          currentList.some((pp) => pp.unique_id === currentPickupPoint);

        // Always validate pickup point, even if points are cached
        // This ensures we catch cases where pickup point is invalid for current provider
        let needsSync = false;

        if (
          hasPointsForCurrentIdentifier &&
          currentPickupPoint &&
          !currentPickupPointExistsInList
        ) {
          // Points cached but pickup point invalid - fetch to validate
          yield self.order.fetchPickupPoints();
          needsSync = self.order._pickupPointNeedsSync;
        } else if (!hasPointsForCurrentIdentifier) {
          // No points loaded - fetch them
          yield self.order.fetchPickupPoints();
          needsSync = self.order._pickupPointNeedsSync;
        } else {
          // Points are cached and pickup point is valid or null - manually validate to ensure correctness
          // This handles cases where provider changed but points were already cached
          yield self.order.handlePickupPointsLoaded();
          needsSync = self.order._pickupPointNeedsSync;
        }

        // Send PUT to sync backend if validation changed the pickup point
        if (needsSync) {
          self.order._pickupPointNeedsSync = false;
          yield self.updateOrder({
            module: CheckoutPageModule.SHIPPING_METHOD_MODULE,
          });
        }
      }),
      openProductPageModal(productId, position) {
        self.checkoutCartModal.setup(productId, position);
        self.checkoutCartModal.toggle(true, true);
      },
    };
  })
  .views((self) => ({
    get cartIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.CART_MODULE) ===
        RequestState.LOADING
      );
    },
    get checkoutIsLoading() {
      return [...self.modulesState.values()].some(
        (state) => state === RequestState.LOADING
      );
    },
    get customerInfoIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.CUSTOMER_INFO_MODULE) ===
        RequestState.LOADING || self.state === RequestState.LOADING
      );
    },
    get shippingInfoIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.SHIPPING_INFO_MODULE) ===
        RequestState.LOADING || self.state === RequestState.LOADING
      );
    },
    get paymentMethodIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.PAYMENT_METHOD_MODULE) ===
        RequestState.LOADING
      );
    },
    get registrationMessages() {
      return self.registrationStatus && self.registrationStatus.messages;
    },
    get shippingMethodIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.SHIPPING_METHOD_MODULE) ===
        RequestState.LOADING
      );
    },
    get summaryIsLoading() {
      return (
        self.modulesState.get(CheckoutPageModule.SUMMARY_MODULE) ===
        RequestState.LOADING
      );
    },
    get orderTotals() {
      return self.order.totals ? [...self.order.totals.values()] : [];
    },
  }));

export default CheckoutStore;
